import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable, of, ReplaySubject, Subject, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { TabSyncService } from './tab-sync.service';
import { APP_CONFIG } from '@pulpo/app-config';
import { RouteReuseStrategy } from '@angular/router';
import { CustomReuseStrategy } from './customRouteReuse.strategy';
import { UserGroupService } from './user-group.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  onLogged$!: Subject<void>;
  url: string = '';

  private refreshInProgress$: ReplaySubject<string> | null = null;

  constructor(
    @Inject(APP_CONFIG) private environment: any,
    private http: HttpClient,
    private storageService: StorageService,
    private tabSyncService: TabSyncService,
    private routeReuseStrategy: RouteReuseStrategy,
    private userGroupService: UserGroupService
  ) {
    this.onLogged$ = new Subject();
    this.url = this.environment.apiUrl;
    if (environment.name === 'localDev') {
      this.url = 'http://localhost:8095';
    }
  }

  login(username: string, password: string): Observable<string> {
    return this.http
      .post(
        this.url + '/user/public/authenticate',
        {
          username,
          password,
          rememberMe: true,
        },
        { observe: 'response' }
      )
      .pipe(
        switchMap((res: any) => {
          if (res && res.body && res.body.accessToken) {
            this.storageService.setToken(res.body.accessToken);
            this.storageService.setRefreshToken(res.body.refreshToken);
            this.storageService.setUser(res.body.user);

            return this.userGroupService
              .getProducerGroup(res.body.user.id)
              .pipe(
                map((response) => {
                  this.storageService.setProducerGroup(response.body!);
                  (
                    this.routeReuseStrategy as CustomReuseStrategy
                  ).clearAllSavedHandles();
                  this.onLogged$.next();
                  return res.body.accessToken;
                })
              );
          } else {
            return new Observable((observer) => {
              observer.next(null);
              observer.complete();
            });
          }
        })
      );
  }

  refreshToken(): Observable<string> {
    // Coordination intra-onglet : si un refresh est déjà en cours ici, attendre son résultat
    if (this.refreshInProgress$) {
      return this.refreshInProgress$.asObservable();
    }

    // Coordination inter-onglets : un autre onglet refresh déjà
    if (this.storageService.isRefreshLocked()) {
      return new Observable<string>(observer => {
        const unsub = this.tabSyncService.listen(
          () => { unsub(); observer.error('logged_out'); },
          (at) => { unsub(); observer.next(at); observer.complete(); }
        );
        const timeout = setTimeout(() => {
          unsub();
          observer.next(this.storageService.getToken());
          observer.complete();
        }, 12_000);
        return () => { unsub(); clearTimeout(timeout); };
      });
    }

    if (!this.storageService.acquireRefreshLock()) {
      return this.refreshToken();
    }

    const refreshToken = this.storageService.getRefreshToken();
    if (!refreshToken) {
      this.storageService.releaseRefreshLock();
      return of(undefined as any);
    }

    this.refreshInProgress$ = new ReplaySubject<string>(1);

    return this.http
      .post(
        this.url + '/user/public/authenticate/refreshtoken',
        { refreshToken },
        { observe: 'response' }
      )
      .pipe(
        switchMap((res: any) => {
          if (res && res.body && res.body.accessToken) {
            this.storageService.setToken(res.body.accessToken);
            this.storageService.setRefreshToken(res.body.refreshToken);
            this.storageService.setUser(res.body.user);
            this.tabSyncService.broadcastTokenRefreshed(res.body.accessToken, res.body.refreshToken);
            this.storageService.releaseRefreshLock();
            this.refreshInProgress$!.next(res.body.accessToken);
            this.refreshInProgress$!.complete();
            this.refreshInProgress$ = null;

            return this.userGroupService
              .getProducerGroup(res.body.user.id)
              .pipe(
                map((response) => {
                  this.storageService.setProducerGroup(response.body!);
                  this.onLogged$.next();
                  return res.body.accessToken;
                })
              );
          } else {
            this.storageService.releaseRefreshLock();
            this.refreshInProgress$!.complete();
            this.refreshInProgress$ = null;
            return of(null);
          }
        }),
        catchError(err => {
          this.storageService.releaseRefreshLock();
          this.refreshInProgress$!.error(err);
          this.refreshInProgress$ = null;
          return throwError(() => err);
        })
      );
  }

  isLoggedIn() {
    return (
      this.storageService.getToken() != null &&
      this.storageService.getTokenExpirationTime() >
        new Date().getTime() / 1000 + 120
    );
  }

  canRefreshToken() {
    return this.storageService.getRefreshToken() != null;
  }

  logout() {
    this.tabSyncService.broadcastLogout();
    this.logoutLocal();
  }

  logoutLocal() {
    this.storageService.clear();
    (this.routeReuseStrategy as CustomReuseStrategy).clearAllSavedHandles();
  }
}
